import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Spinner from "@/components/common/spinner/Spinner";
import ApiErrorAlert from "@/components/layout/message/ApiErrorAlert";
import AlertMessage from "@/components/layout/message/AlertMessage";
import Button from "@/components/common/button/Button";
import PaginationControls from "@/components/layout/paginationControls/PaginationControls";

import styles from "./StudentSimulationManagement.module.css";
import ManagementHeader from "../admin/managementHeader/ManagementHeader";
import { useStudentSimulationManagement } from "./useStudentSimulationManagement";
import AnswersTable, { AnswerFilters } from "./components/answersTable/AnswersTable";

const StudentSimulationManagement: React.FC = () => {
    useEffect(() => {
        document.body.classList.add("solid-public-bg");
        return () => document.body.classList.remove("solid-public-bg");
    }, []);

    const {
        state,
        availableTests,
        paginatedQuestions,
        totalQuestions,
        pageSize,
        handleSessionChange,
        handleTestChange,
        handleAnswerChange,
        handleSave,
        setCurrentPage,
    } = useStudentSimulationManagement();

    const {
        isLoading,
        isSaving,
        error,
        simulationData,
        filters,
        currentPage,
        answers,
    } = state;
    const navigate = useNavigate();
    const { userId } = useParams<{ userId: string }>();

    const onSave = async () => {
        if (simulationData.isClosed) return;

        try {
            await handleSave();
            alert("Respuestas guardadas exitosamente.");
            navigate(-1);
        } catch (err) { }
    };

    if (isLoading && simulationData.sessions.length === 0) {
        return <Spinner text="Cargando configuración del simulacro..." />;
    }

    const isReadOnly = simulationData.isClosed;

    return (
        <div className={styles["page-container"]}>
            <ManagementHeader
                linkTo={`/facilitator-panel/user-management/${userId}/assignments`}
            />
            <main className={styles["main-content"]}>
                <h1 className={styles["page-title"]}>
                    Gestionar respuestas del estudiante
                </h1>
                <ApiErrorAlert error={error} />
                {isReadOnly && (
                    <AlertMessage type="info" title="Modo de solo lectura">
                        Este simulacro está cerrado y las respuestas no se pueden modificar.
                    </AlertMessage>
                )}


                <AnswerFilters
                    sessions={simulationData.sessions}
                    availableTests={availableTests}
                    selectedSessionId={filters.sessionId}
                    selectedTestId={filters.testId}
                    onSessionChange={handleSessionChange}
                    onTestChange={handleTestChange}
                />

                {!isLoading && (
                    <>
                        <AnswersTable
                            questions={paginatedQuestions}
                            answers={answers}
                            onAnswerChange={handleAnswerChange}
                            disabled={isReadOnly}
                        />

                        <PaginationControls
                            currentPage={currentPage}
                            totalCount={totalQuestions}
                            pageSize={pageSize}
                            onPageChange={setCurrentPage}
                        />
                        {!isReadOnly && (
                            <div className={styles.actionsContainer}>
                                <Button onClick={onSave} isLoading={isSaving} disabled={isSaving}>
                                    Guardar cambios
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </main>
        </div>
    );
};

export default StudentSimulationManagement;
